import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, query, collection, where, getDocs } from "firebase/firestore";
import { Header } from "../../components/Header/Header";
import { Footer } from "../../components/Footer/Footer";
import { db } from "../../../service/firebase";
import { useAuth } from "../../../context/AuthContext";
import "./TurmaEstatisticas.css";

const TurmaEstatisticas = () => {
  const { id: turmaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [turma, setTurma] = useState(null);
  const [membros, setMembros] = useState([]);

  useEffect(() => {
    const carregarDados = async () => {
      if (!user) {
        setError("Você precisa estar autenticado para ver esta página.");
        setLoading(false);
        return;
      }

      setError("");
      setLoading(true);
      try {
        // Carregar dados da turma
        const turmaDoc = await getDoc(doc(db, "turmas", turmaId));
        if (!turmaDoc.exists()) {
          setError("Turma não encontrada.");
          setLoading(false);
          return;
        }

        setTurma({ id: turmaDoc.id, ...turmaDoc.data() });

        // Carregar membros da turma com estatísticas
        const membrosQuery = query(
          collection(db, "jogador_turma"),
          where("id_turma", "==", turmaId),
          where("status", "==", 1) // Apenas membros ativos
        );
        const membrosSnapshot = await getDocs(membrosQuery);
        
        const membrosComUsuarios = await Promise.all(
          membrosSnapshot.docs.map(async (docSnapshot) => {
            const membroData = { id: docSnapshot.id, ...docSnapshot.data() };
            
            // Buscar dados do usuário
            try {
              const usuarioDoc = await getDoc(doc(db, "usuarios", membroData.id_usuario));
              if (usuarioDoc.exists()) {
                const usuarioData = usuarioDoc.data();
                return {
                  ...membroData,
                  nome: usuarioData.nickname || usuarioData.displayName || usuarioData.nome || usuarioData.email || "Jogador",
                  posicao: usuarioData.posicao || "Não informada"
                };
              }
            } catch (err) {
              console.error("Erro ao buscar usuário:", err);
            }
            
            return {
              ...membroData,
              nome: "Jogador",
              posicao: "Não informada"
            };
          })
        );

        setMembros(membrosComUsuarios);
      } catch (err) {
        console.error("Erro ao carregar estatísticas da turma", err);
        setError("Não foi possível carregar as estatísticas da turma.");
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [turmaId, user]);

  // Calcular pontos de cada jogador
  const calcularPontos = (membro) => {
    const vitorias = (membro.vitorias || 0) * 3;
    const empates = (membro.empates || 0) * 1;
    const derrotas = 0; // Derrotas não valem pontos
    const gols = (membro.gols || 0) * 2;
    const assistencias = (membro.assistencias || 0) * 1;
    const jogosSemTomarGol = (membro.jogosSemTomarGol || 0) * 1;
    
    return vitorias + empates + derrotas + gols + assistencias + jogosSemTomarGol;
  };

  // Ranking completo ordenado por pontos
  const ranking = useMemo(() => {
    return membros
      .map((membro) => ({
        ...membro,
        pontos: calcularPontos(membro)
      }))
      .sort((a, b) => b.pontos - a.pontos);
  }, [membros]);

  // Destaques
  const artilheiro = useMemo(() => {
    if (membros.length === 0) return null;
    return [...membros].sort((a, b) => (b.gols || 0) - (a.gols || 0))[0];
  }, [membros]);

  const liderAssistencias = useMemo(() => {
    if (membros.length === 0) return null;
    return [...membros].sort((a, b) => (b.assistencias || 0) - (a.assistencias || 0))[0];
  }, [membros]);

  const jogadorMaisVitorias = useMemo(() => {
    if (membros.length === 0) return null;
    return [...membros].sort((a, b) => (b.vitorias || 0) - (a.vitorias || 0))[0];
  }, [membros]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="body">
          <div className="center">
            <div className="body-content card turma-estatisticas-card">
              <span>Carregando estatísticas...</span>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="body">
          <div className="center">
            <div className="body-content card turma-estatisticas-card">
              <p className="turma-error">{error}</p>
              <button className="btn-default" onClick={() => navigate(-1)}>
                Voltar
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="body">
        <div className="center">
          <div className="body-content card turma-estatisticas-card">
            <div className="turma-estatisticas-header">
              <button
                className="btn-back"
                onClick={() => navigate(`/turmas/${turmaId}`)}
              >
                ← Voltar
              </button>
              <h2>Estatísticas - {turma?.nome}</h2>
            </div>

            {/* Seção de Destaques */}
            <div className="estatisticas-destaques">
              <h3>Destaques da Turma</h3>
              <div className="destaques-grid">
                <div className="destaque-card">
                  <div className="destaque-icon">⚽</div>
                  <div className="destaque-content">
                    <span className="destaque-label">Artilheiro</span>
                    <strong className="destaque-nome">
                      {artilheiro?.nome || "—"}
                    </strong>
                    <span className="destaque-valor">
                      {artilheiro?.gols || 0} gols
                    </span>
                  </div>
                </div>

                <div className="destaque-card">
                  <div className="destaque-icon">🎯</div>
                  <div className="destaque-content">
                    <span className="destaque-label">Líder de Assistências</span>
                    <strong className="destaque-nome">
                      {liderAssistencias?.nome || "—"}
                    </strong>
                    <span className="destaque-valor">
                      {liderAssistencias?.assistencias || 0} assistências
                    </span>
                  </div>
                </div>

                <div className="destaque-card">
                  <div className="destaque-icon">🏆</div>
                  <div className="destaque-content">
                    <span className="destaque-label">Mais Vitórias</span>
                    <strong className="destaque-nome">
                      {jogadorMaisVitorias?.nome || "—"}
                    </strong>
                    <span className="destaque-valor">
                      {jogadorMaisVitorias?.vitorias || 0} vitórias
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção de Ranking Completo */}
            <div className="estatisticas-ranking">
              <h3>Ranking Completo</h3>
              <div className="ranking-info">
                <p>
                  <strong>Sistema de Pontuação:</strong> Vitória (3pts) • Empate (1pt) • 
                  Gol (2pts) • Assistência (1pt) • Partida sem tomar gol (1pt)
                </p>
              </div>
              
              {ranking.length === 0 ? (
                <div className="ranking-empty">
                  <p>Ainda não há estatísticas registradas para esta turma.</p>
                </div>
              ) : (
                <div className="ranking-table-wrapper">
                  <table className="ranking-table">
                    <thead>
                      <tr>
                        <th className="rank-pos">#</th>
                        <th className="rank-nome">Nome</th>
                        <th className="rank-stat">Pts</th>
                        <th className="rank-stat">V</th>
                        <th className="rank-stat">E</th>
                        <th className="rank-stat">G</th>
                        <th className="rank-stat">A</th>
                        <th className="rank-stat">SG</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranking.map((membro, index) => (
                        <tr key={membro.id} className={index < 3 ? `top-${index + 1}` : ""}>
                          <td className="rank-pos">
                            {index + 1}
                            {index === 0 && " 🥇"}
                            {index === 1 && " 🥈"}
                            {index === 2 && " 🥉"}
                          </td>
                          <td className="rank-nome">{membro.nome}</td>
                          <td className="rank-stat rank-pontos">{membro.pontos}</td>
                          <td className="rank-stat">{membro.vitorias || 0}</td>
                          <td className="rank-stat">{membro.empates || 0}</td>
                          <td className="rank-stat">{membro.gols || 0}</td>
                          <td className="rank-stat">{membro.assistencias || 0}</td>
                          <td className="rank-stat">{membro.jogosSemTomarGol || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Legenda */}
            <div className="ranking-legenda">
              <p>
                <strong>Legenda:</strong> Pts = Pontos | V = Vitórias | E = Empates | 
                G = Gols | A = Assistências | SG = Sem tomar gol
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TurmaEstatisticas;
